import React from "react";
import { AlertTriangle, Loader2, XCircle } from "lucide-react";
import Consultant from "@/models/Consultant";
import { PreviewPersonFileTemplateResponse } from "@/models/PersonFileTemplate";
import ConsultantPicker from "./ConsultantPicker";

interface Props {
	consultant?: Consultant;
	onConsultantChange: (consultant: Consultant) => void;
	data?: PreviewPersonFileTemplateResponse;
	loading: boolean;
	/** Set when the preview request itself failed (network / server error). */
	requestError?: string;
}

/** Red panel for a template that could not be compiled or rendered. */
const ErrorPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
		<XCircle size={14} className="mt-0.5 shrink-0" />
		<pre className="whitespace-pre-wrap font-mono">{children}</pre>
	</div>
);

/** Amber panel for advisory remarks — these never block saving. */
export const WarningPanel: React.FC<{ warnings: string[] }> = ({ warnings }) =>
	warnings.length ? (
		<div className="space-y-1">
			{warnings.map((warning) => (
				<div
					key={warning}
					className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
				>
					<AlertTriangle size={14} className="mt-0.5 shrink-0" />
					<span>{warning}</span>
				</div>
			))}
		</div>
	) : null;

/**
 * Right-hand column of the person-file editor: pick a consultant, see the
 * lines the current draft would produce for them.
 */
const PersonFilePreview: React.FC<Props> = ({
	consultant,
	onConsultantChange,
	data,
	loading,
	requestError,
}) => (
	<div className="space-y-3">
		<div className="space-y-1">
			<label className="text-xs font-medium text-gray-600">Förhandsgranska för konsult</label>
			<ConsultantPicker value={consultant} onChange={onConsultantChange} />
		</div>

		{!consultant ? (
			<div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-6 text-center text-xs text-gray-500">
				Välj en konsult ovan för att se vad skriptet skriver ut.
			</div>
		) : (
			<>
				{requestError && <ErrorPanel>{requestError}</ErrorPanel>}
				{data?.error && <ErrorPanel>{data.error}</ErrorPanel>}
				{data && <WarningPanel warnings={data.warnings} />}

				<div className="relative overflow-hidden rounded-md border border-gray-200 bg-white">
					{loading && (
						<div className="absolute right-2 top-2 flex items-center gap-1 rounded bg-white/90 px-2 py-1 text-[11px] text-gray-400">
							<Loader2 size={12} className="animate-spin" /> Renderar…
						</div>
					)}
					<pre className="max-h-[calc(100vh-26rem)] min-h-[12rem] overflow-auto overflow-x-auto p-3 font-mono text-xs leading-5 text-gray-800">
						{data?.rendered ?? (loading ? "" : "Ingen utdata")}
					</pre>
				</div>
			</>
		)}
	</div>
);

export default PersonFilePreview;
